import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import GoldenRetriever from '@uppy/golden-retriever';
import fr_FR from '@uppy/locales/lib/fr_FR';
import type { ReactNode } from 'react';

import { EMPTY_STRING, NULL_FUNCTION } from '@e-socna/empty';

import { environment } from '../../environment';

import { buildUppyUploadInstanceId } from './utils/build-uppy-upload-instance-id.util';
import { countActiveFiles } from './utils/count-active-files.util';
import { hasErroredFiles } from './utils/has-errored-files.util';
import type { UppyUploadInstanceIdKey } from './utils/build-uppy-upload-instance-id.util';
import type { UppyUploadInstance } from './types/uppy-upload-instance.type';
import type { UppyUploadScope } from './types/uppy-upload-scope.type';

// Shared across every scope's Uppy instance, so a handful of missions uploading at the
// same time don't collectively saturate the browser's network/memory.
const UPLOAD_PARALLEL_LIMIT = 3;
const UPLOAD_CHUNK_SIZE_BYTES = 50_000_000;
// Service worker storage is temporary and does not survive a real browser crash/restart -
// it only protects against an accidental tab close/refresh while files are still selected.
const GOLDEN_RETRIEVER_EXPIRY_MS = 48 * 60 * 60 * 1000;
// Built from apps/front/src/sw.ts by vite-plugin-pwa - see vite.config.ts.
const GOLDEN_RETRIEVER_SERVICE_WORKER_URL = '/sw.js';
// Failures from the same root cause (auth expired, server down...) tend to land within
// milliseconds of each other across an entire batch - this groups them into one toast per
// distinct message instead of flooding the screen with one toast per file.
const UPLOAD_ERROR_BATCH_DEBOUNCE_MS = 500;

interface PendingUploadError {
  readonly fileName: string;
  readonly message: string;
}

interface UppyUploadContextData {
  readonly instances: readonly UppyUploadInstance[];
  readonly activeFilesCount: number;
  readonly hasUploadError: boolean;
  readonly getUploadInstance: (scope: Readonly<UppyUploadScope>) => Uppy;
  readonly releaseUploadInstance: (
    key: Readonly<UppyUploadInstanceIdKey>,
  ) => void;
}

const UppyUploadContext = createContext<UppyUploadContextData>({
  instances: [],
  activeFilesCount: 0,
  hasUploadError: false,
  getUploadInstance: () => new Uppy(),
  releaseUploadInstance: NULL_FUNCTION,
});

const createUploadInstance = (scope: Readonly<UppyUploadScope>) => {
  const instance = new Uppy({
    id: buildUppyUploadInstanceId(scope),
    locale: fr_FR,
  })
    .use(Tus, {
      endpoint: `${environment.backendUrl}/${scope.tusCategory}/tus`,
      withCredentials: true,
      limit: UPLOAD_PARALLEL_LIMIT,
      chunkSize: UPLOAD_CHUNK_SIZE_BYTES,
    })
    .use(GoldenRetriever, {
      expires: GOLDEN_RETRIEVER_EXPIRY_MS,
      serviceWorker: true,
    });

  instance.setMeta({
    entityId: String(scope.entityId),
    fileCategoryId: String(scope.fileCategoryId),
  });

  return instance;
};

interface Props {
  readonly children: ReactNode;
}

// Registry of Uppy instances kept alive for the whole logged-in session (above the
// router), keyed by entity/category scope. Each scope gets its own dedicated instance -
// and therefore its own Dashboard state, isolated from every other mission/category - but
// navigating away from a page (or switching the selected category within the same page)
// never tears one down as long as it was actually used, so its upload keeps running in the
// background regardless. An instance nobody ever added a file to is reclaimed instead of
// leaking for the rest of the session - see releaseUploadInstance.
const UppyUploadContextProvider = ({ children }: Readonly<Props>) => {
  const instancesRef = useRef(new Map<string, UppyUploadInstance>());
  const [instances, setInstances] = useState<readonly UppyUploadInstance[]>([]);
  const [activeFilesCount, setActiveFilesCount] = useState(0);
  const [hasUploadError, setHasUploadError] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const pendingUploadErrorsRef = useRef<PendingUploadError[]>([]);
  const uploadErrorFlushTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }
    navigator.serviceWorker
      .register(GOLDEN_RETRIEVER_SERVICE_WORKER_URL, { type: 'module' })
      .catch(NULL_FUNCTION);
  }, []);

  useEffect(
    () => () => {
      const instanceMap = instancesRef.current;
      instanceMap.forEach(({ uppy }) => {
        uppy.destroy();
      });
      instanceMap.clear();
    },
    [],
  );

  useEffect(() => {
    // A freshly registered instance starts with no files, so it can't change the current
    // active count - no need to recompute synchronously here, only react to future updates.
    const recomputeUploadState = () => {
      const activeCount = instances.reduce(
        (total, { uppy }) => total + countActiveFiles(uppy),
        0,
      );
      setActiveFilesCount(activeCount);
      setHasUploadError(instances.some(({ uppy }) => hasErroredFiles(uppy)));
      // Golden Retriever only recovers from an accidental close/refresh, not a real crash -
      // still worth warning the user before they close the tab on a multi-minute upload.
      window.onbeforeunload = activeCount > 0 ? () => EMPTY_STRING : null;
    };

    instances.forEach(({ uppy }) => {
      uppy.on('state-update', recomputeUploadState);
    });

    return () => {
      instances.forEach(({ uppy }) => {
        uppy.off('state-update', recomputeUploadState);
      });
    };
  }, [instances]);

  useEffect(() => {
    // Invalidates the queries this instance's page cares about regardless of whether that
    // page (and the FilesUploadDashboard that started the upload) is still mounted - the
    // instance itself is the only thing guaranteed to survive navigating away, so this is
    // the sole place that can reliably refresh e.g. the sidebar's file count after a
    // background upload finishes.
    const detachers = instances.map(({ scope, uppy }) => {
      const handleComplete = (
        result: Readonly<{ successful?: readonly unknown[] }>,
      ) => {
        if (!result.successful || result.successful.length === 0) {
          return;
        }
        scope.queryDocumentNamesToInvalidate.forEach((queryDocumentName) => {
          void queryClient.invalidateQueries({
            queryKey: [queryDocumentName],
          });
        });
      };

      uppy.on('complete', handleComplete);
      return () => {
        uppy.off('complete', handleComplete);
      };
    });

    return () => {
      detachers.forEach((detach) => {
        detach();
      });
    };
  }, [instances, queryClient]);

  useEffect(() => {
    // Surfaces upload failures as a toast regardless of whether this instance's Dashboard
    // (or the global tray) happens to be mounted - otherwise a background upload failing
    // while the user is on another page would fail silently.
    const flushPendingUploadErrors = () => {
      const pendingErrors = pendingUploadErrorsRef.current;
      pendingUploadErrorsRef.current = [];
      uploadErrorFlushTimeoutRef.current = null;

      const fileNamesByMessage = new Map<string, string[]>();
      pendingErrors.forEach(({ fileName, message }) => {
        const fileNames = fileNamesByMessage.get(message) ?? [];
        fileNamesByMessage.set(message, [...fileNames, fileName]);
      });

      fileNamesByMessage.forEach((fileNames, message) => {
        enqueueSnackbar(
          fileNames.length === 1
            ? `Échec de l'envoi de « ${fileNames[0] ?? 'fichier'} » : ${message}`
            : `Échec de l'envoi de ${String(fileNames.length)} fichiers : ${message}`,
          { variant: 'error' },
        );
      });
    };

    const queueUploadError = (fileName: string, message: string) => {
      pendingUploadErrorsRef.current = [
        ...pendingUploadErrorsRef.current,
        { fileName, message },
      ];
      if (uploadErrorFlushTimeoutRef.current) {
        clearTimeout(uploadErrorFlushTimeoutRef.current);
      }
      uploadErrorFlushTimeoutRef.current = setTimeout(
        flushPendingUploadErrors,
        UPLOAD_ERROR_BATCH_DEBOUNCE_MS,
      );
    };

    const handleUploadError = (
      file: Readonly<{ name?: string }> | undefined,
      error: Readonly<{ message: string }>,
    ) => {
      queueUploadError(file?.name ?? 'fichier', error.message);
    };

    const handleRestrictionFailed = (
      file: Readonly<{ name?: string }> | undefined,
      error: Readonly<{ message: string }>,
    ) => {
      queueUploadError(file?.name ?? 'fichier', error.message);
    };

    instances.forEach(({ uppy }) => {
      uppy.on('upload-error', handleUploadError);
      uppy.on('restriction-failed', handleRestrictionFailed);
    });

    return () => {
      instances.forEach(({ uppy }) => {
        uppy.off('upload-error', handleUploadError);
        uppy.off('restriction-failed', handleRestrictionFailed);
      });
    };
  }, [instances, enqueueSnackbar]);

  const getUploadInstance = useCallback((scope: Readonly<UppyUploadScope>) => {
    const id = buildUppyUploadInstanceId(scope);
    const existingInstance = instancesRef.current.get(id);
    if (existingInstance) {
      // Labels can arrive after the instance was first created (e.g. data still loading).
      // Keep the tray showing the latest ones for this scope.
      if (
        existingInstance.scope.label !== scope.label ||
        existingInstance.scope.fileCategoryLabel !== scope.fileCategoryLabel
      ) {
        instancesRef.current.set(id, { ...existingInstance, scope });
        setInstances(Array.from(instancesRef.current.values()));
      }
      return existingInstance.uppy;
    }

    const uppy = createUploadInstance(scope);
    instancesRef.current.set(id, { id, scope, uppy });
    setInstances(Array.from(instancesRef.current.values()));
    return uppy;
  }, []);

  const releaseUploadInstance = useCallback(
    (key: Readonly<UppyUploadInstanceIdKey>) => {
      const id = buildUppyUploadInstanceId(key);
      const existingInstance = instancesRef.current.get(id);
      // Only reclaim instances nobody ever added a file to - an instance with files (queued,
      // uploading or completed) must survive its consumer unmounting, since that's the whole
      // point of the registry: uploads keep running when the user navigates away.
      if (!existingInstance || existingInstance.uppy.getFiles().length > 0) {
        return;
      }

      existingInstance.uppy.destroy();
      instancesRef.current.delete(id);
      setInstances(Array.from(instancesRef.current.values()));
    },
    [],
  );

  const value = useMemo(
    () => ({
      instances,
      activeFilesCount,
      hasUploadError,
      getUploadInstance,
      releaseUploadInstance,
    }),
    [
      instances,
      activeFilesCount,
      hasUploadError,
      getUploadInstance,
      releaseUploadInstance,
    ],
  );

  return (
    <UppyUploadContext.Provider value={value}>
      {children}
    </UppyUploadContext.Provider>
  );
};

export { UppyUploadContext, UppyUploadContextProvider };
